import { Pagination, Table } from 'antd';
import Column from 'antd/es/table/Column';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import {
  DeleteActionSvg,
  EditActionSvg,
  EyeIcon,
  SortBottomIcon,
  SortTopIcon,
  TableNextIcon,
  TablePreviousIcon,
} from 'utils/svgs';
import { checkPermission } from 'utils/utils';
import SearchAndFilter from '../SearchAndFilter';
import styles from '../../Medicines/CustomTable/index.module.scss';
import './index.scss';
import { CustomComboTableProps } from './type';

const CustomTable: FC<CustomComboTableProps> = (props) => {
  const {
    dataSource,
    handleTableChange,
    onShowSizeChange,
    isLoading,
    search,
    totalRecords,
    performSearchHandler,
    currentPage,
    pageSize,
    setSearch,
    viewHandler,
    openDrawerHandler,
    setEditSelectedCombo,
    setTitle,
    showModal,
    statusFilter,
    onStatusFilterChange,
    categoryFilter,
    onCategoryFilterChange,
    subCategoryFilter,
    onSubCategoryFilterChange,
    categories,
    selectedDate,
    setSelectedDate,
  } = props;

  const userPermissions = useSelector((state: any) => state?.userProfile);

  const isViewPermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'ComboItems',
    permission: ['view', 'viewAll'],
  });

  const isEditPermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'ComboItems',
    permission: ['edit'],
  });

  const isImageEditPermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'Images',
    permission: ['edit'],
  });

  const canEdit = isEditPermission || isImageEditPermission;

  const isDeletePermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'ComboItems',
    permission: ['delete'],
  });

  const editDataHandler = (item: any) => {
    openDrawerHandler();
    setTitle('Edit Combo');
    setEditSelectedCombo(item);
  };

  const deleteDataHandler = (item: any) => {
    setEditSelectedCombo(item);
    showModal();
  };

  return (
    <>
      <div className={`${styles['body']} user-table`}>
        <SearchAndFilter
          performSearchHandler={performSearchHandler}
          searchValue={search}
          openDrawerHandler={openDrawerHandler}
          title="Combo List"
          setSearch={setSearch}
          totalRecords={totalRecords}
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={onCategoryFilterChange}
          subCategoryFilter={subCategoryFilter}
          onSubCategoryFilterChange={onSubCategoryFilterChange}
          categories={categories}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />

        <Table
          rowKey={(record: any) => record?.id}
          dataSource={dataSource}
          pagination={false}
          loading={isLoading}
          onChange={handleTableChange}
          tableLayout="fixed"
          scroll={{ y: 'calc(100vh - 300px)' }}
        >
          <Column
            title={<p style={{ padding: '0 10px' }}>Combo Name</p>}
            dataIndex="comboName"
            key="comboName"
            className="user-column bg-white"
            width="16%"
            ellipsis
            sorter
            sortDirections={['ascend', 'descend', 'ascend']}
            render={(value: string) => value || '-'}
            sortIcon={({ sortOrder }) => (
              <>
                <SortTopIcon fill={sortOrder === 'ascend' ? '#949494' : undefined} />
                <SortBottomIcon fill={sortOrder === 'descend' ? '#949494' : undefined} />
              </>
            )}
          />
          <Column
            title="Brand"
            dataIndex="brand"
            key="brand"
            className="bg-white"
            width="10%"
            ellipsis
            render={(value: string) => value || '-'}
          />
          <Column
            title="Manufacturer"
            dataIndex="manufacturerName"
            key="manufacturerName"
            className="bg-white"
            width="12%"
            ellipsis
            render={(value: string) => value || '-'}
          />
          <Column
            title="MRP"
            dataIndex="mrp"
            key="mrp"
            className="bg-white"
            width="8%"
            render={(value: number) => (value != null ? value : '-')}
          />
          <Column
            title="Tax Rate"
            dataIndex="taxRate"
            key="taxRate"
            className="bg-white"
            width="8%"
            render={(value: number) => (value != null ? value : '-')}
          />
          <Column
            title="Category"
            dataIndex="categoryName"
            key="categoryName"
            className="bg-white"
            width="11%"
            ellipsis
            render={(value: string) => value || '-'}
          />
          <Column
            title="Image"
            dataIndex="image"
            key="image"
            className="bg-white"
            width="6%"
            align="center"
            render={(_: unknown, record: any) => {
              const hasPrimary =
                record?.comboPrimaryImageUrl &&
                String(record.comboPrimaryImageUrl).trim() !== '';
              const hasSecondary =
                Array.isArray(record?.comboSecondaryImageUrl) &&
                record.comboSecondaryImageUrl.length > 0;
              if (hasPrimary || hasSecondary) {
                return <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />;
              }
              return <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />;
            }}
          />
          <Column
            title="Quality Check"
            dataIndex="qualityCheck"
            key="qualityCheck"
            className="bg-white"
            width="9%"
            render={(value: string) => {
              if (!value) return '-';
              const normalized = value.toUpperCase();
              const statusClass =
                normalized === 'PENDING'
                  ? styles.status__pending
                  : normalized === 'REJECTED'
                    ? styles.status__rejected
                    : styles.status__approved;
              return <span className={statusClass}>{value}</span>;
            }}
          />
          {(isViewPermission || canEdit || isDeletePermission) && (
            <Column
              title="Action"
              dataIndex="action"
              key="action"
              className="bg-white combo-table-action-col"
              width="11%"
              align="center"
              render={(_: unknown, data: any) => (
                <div className="combo-table-actions">
                  {isViewPermission ? (
                    <div
                      className="cursor-pointer flex align-center justify-center"
                      onClick={() => viewHandler(data)}
                    >
                      <EyeIcon />
                    </div>
                  ) : (
                    <div className="cursor-not-allowed flex align-center justify-center">
                      <EyeIcon />
                    </div>
                  )}

                  {canEdit ? (
                    <div
                      className="cursor-pointer flex align-center justify-center rounded-action-btn"
                      onClick={() => editDataHandler(data)}
                    >
                      <EditActionSvg />
                    </div>
                  ) : (
                    <div className="cursor-not-allowed flex align-center justify-center rounded-action-btn">
                      <EditActionSvg />
                    </div>
                  )}

                  {isDeletePermission ? (
                    <div
                      className="cursor-pointer flex align-center justify-center rounded-action-btn"
                      onClick={() => deleteDataHandler(data)}
                    >
                      <DeleteActionSvg />
                    </div>
                  ) : (
                    <div className="cursor-not-allowed flex align-center justify-center rounded-action-btn">
                      <DeleteActionSvg />
                    </div>
                  )}
                </div>
              )}
            />
          )}
        </Table>

        <div className={styles['pagination__wrapper']}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            showSizeChanger
            onChange={onShowSizeChange}
            total={totalRecords}
            itemRender={(page, type, originalElement) => (
              <div className={styles['pagination']}>
                {type === 'prev' ? (
                  <button
                    type="button"
                    className={`${styles['pagination']} ${styles['pagination__button']}`}
                  >
                    <TablePreviousIcon />
                    <p className={styles['pagination__previous']}>Previous</p>
                  </button>
                ) : type === 'next' ? (
                  <button
                    type="button"
                    className={`${styles['pagination']} ${styles['pagination__button']}`}
                  >
                    <p className={styles['pagination__next']}>Next</p>
                    <TableNextIcon />
                  </button>
                ) : (
                  originalElement
                )}
              </div>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default CustomTable;
